// src/services/api.js
class ApiService {
  constructor() {
    // Use same-origin (the nginx proxy) in production so API/WS calls go to
    // https://www.jv-games.com/... instead of the dead api.jv-games.com host.
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    this.baseURL = process.env.REACT_APP_API_URL || origin;
    this.wsURL = process.env.REACT_APP_WS_URL || origin;
    
    // Log for debugging
    console.log('API Service initialized:', {
      baseURL: this.baseURL,
      wsURL: this.wsURL
    });
  }

  // Get auth token from localStorage or context
  getToken() {
    return localStorage.getItem('token') || sessionStorage.getItem('token');
  }

  // Common headers for all requests
  getHeaders(additionalHeaders = {}) {
    const token = this.getToken();
    const headers = {
      'Content-Type': 'application/json',
      ...additionalHeaders
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  // Handle API responses for JSON
  async handleJsonResponse(response) {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const detailMsg = Array.isArray(errorData.details) && errorData.details.length
        ? errorData.details.map((d) => d.message || d.field).join('; ')
        : '';
      throw new Error(detailMsg || errorData.error || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  // Handle API responses for blobs (file downloads)
  async handleBlobResponse(response) {
    if (!response.ok) {
      const errorData = await response.text().catch(() => ({}));
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.blob();
  }

  // GET request for JSON
  async get(url) {
    const response = await fetch(`${this.baseURL}${url}`, {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    });
    return this.handleJsonResponse(response);
  }

  // POST request for JSON
  async post(url, data) {
    const response = await fetch(`${this.baseURL}${url}`, {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return this.handleJsonResponse(response);
  }

  // PUT request for JSON
  async put(url, data) {
    const response = await fetch(`${this.baseURL}${url}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return this.handleJsonResponse(response);
  }

  // DELETE request
  async delete(url) {
    const response = await fetch(`${this.baseURL}${url}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
      credentials: 'include'
    });
    return this.handleJsonResponse(response);
  }

  // PATCH request for JSON
  async patch(url, data) {
    const response = await fetch(`${this.baseURL}${url}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return this.handleJsonResponse(response);
  }


  // GET request for file download (blob)
  async getBlob(url) {
    const response = await fetch(`${this.baseURL}${url}`, {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    });
    return this.handleBlobResponse(response);
  }

  // POST request for file download (blob)
  async postBlob(url, data) {
    const response = await fetch(`${this.baseURL}${url}`, {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return this.handleBlobResponse(response);
  }

  // Get WebSocket URL for Socket.IO connections
  getWebSocketURL() {
    // For Socket.IO, we use the same HTTP URL (Socket.IO handles protocol upgrade)
    console.log('Socket.IO URL:', this.wsURL);
    return this.wsURL;
  }

  // Get native WebSocket URL (if needed for non-Socket.IO connections)
  getNativeWebSocketURL() {
    // Convert http:// to ws:// and https:// to wss:// for native WebSockets
    let nativeWsUrl = this.wsURL;
    if (nativeWsUrl.startsWith('http://')) {
      nativeWsUrl = nativeWsUrl.replace('http://', 'ws://');
    } else if (nativeWsUrl.startsWith('https://')) {
      nativeWsUrl = nativeWsUrl.replace('https://', 'wss://');
    }
    
    console.log('Native WebSocket URL:', nativeWsUrl);
    return nativeWsUrl;
  }

  // Admin-specific export methods
  async exportToExcel(filters) {
    return this.postBlob('/api/admin/export/excel', filters);
  }

  async exportToPDF(filters) {
    return this.postBlob('/api/admin/export/pdf', filters);
  }

  // Auth specific methods
  async login(email, password) {
    return this.post('/api/auth/login', { email, password });
  }

  async register(userData) {
    return this.post('/api/auth/register', userData);
  }

  async logout() {
    return this.post('/api/auth/logout');
  }

  async getProfile() {
    return this.get('/api/auth/profile');
  }

  async refreshToken() {
    return this.post('/api/auth/refresh');
  }
}

// Create and export the instance
const apiService = new ApiService();
export default apiService;

// Also export the class and named methods for flexibility
export { ApiService };